/**
 * The user-role chat message the onboarding client posts after a deck upload.
 * The app writes it, not the founder, so it carries no founder facts; the deck
 * parse owns the upload's facts.
 *
 * `neutral` (LEV-2517): a program lander's drop zone invites the founder's
 * current application, not only a pitch deck, so a PDF/PPTX dropped there may
 * not be a deck at all. Programs get the file-neutral prefix; the homepage
 * composer never passes `neutral` and keeps the original wording byte-for-byte.
 *
 * Cross-compatible: no "use client", no runtime imports. The client composes
 * with `composeDeckUploadNotice`; the server recognises it with
 * `isDeckUploadNotice`. Both MUST change together: a reworded composer that the
 * matcher misses sends the notice back to the chat extractor.
 */

const PREFIX = `Here's my pitch deck ("`;
const NEUTRAL_PREFIX = `Here's my file ("`;
const SUFFIX = `").`;

export function composeDeckUploadNotice(
  fileName: string,
  options?: { neutral?: boolean },
): string {
  const prefix = options?.neutral ? NEUTRAL_PREFIX : PREFIX;
  return `${prefix}${fileName}${SUFFIX}`;
}

/**
 * True only for the whole composed notice, under either prefix. Anchored on
 * both ends so founder text that merely mentions a pitch deck or a file is
 * never skipped.
 */
export function isDeckUploadNotice(text: string): boolean {
  const trimmed = text.trim();
  const prefix = trimmed.startsWith(PREFIX)
    ? PREFIX
    : trimmed.startsWith(NEUTRAL_PREFIX)
      ? NEUTRAL_PREFIX
      : null;
  return (
    prefix !== null &&
    trimmed.endsWith(SUFFIX) &&
    trimmed.length > prefix.length + SUFFIX.length &&
    !trimmed.includes("\n")
  );
}
